import React from 'react';

import styles from './DrawerContent.scss';

import cx from 'classnames';
import { Entry, ReferencedEntry } from 'contentful';

import {
  ContentType,
  Link,
  LinkSet,
  Product,
} from 'src/api/contentful/contentTypes.cloverdotcom';
import { Entries } from 'src/api/contentful/utilTypes';
import { getContentType } from 'src/utils/CloverdotcomEntryUtil';
import { getField, isEntry } from 'src/utils/EntryUtil';

import { HeaderItem } from './HeaderItem/HeaderItem';
import { MiscLinksGrid } from './MiscLinksGrid/MiscLinksGrid';
import { ProductsGrid } from './ProductsGrid/ProductsGrid';

export interface DrawerContentProps extends React.AllHTMLAttributes<HTMLDivElement> {
  entry?: ReferencedEntry<LinkSet>;
  active?: boolean;
  tablet?: boolean;
  mobile?: boolean;
  desktop?: boolean;
}

export const categorize = (links: Entries<Link> = []) => links.reduce((categories, link) => {
  if (!isEntry(link)) return categories;
  switch (getContentType(link)) {
    case ContentType.PRODUCT:
      categories.productLinks.push(link);
      break;
    default:
      categories.otherLinks.push(link);
      break;
  }
  return categories;
}, {
  productLinks: [] as Array<Entry<Product>>,
  otherLinks: [] as Array<Entry<Link>>,
});

export const DrawerContent: React.FC<DrawerContentProps> = ({
  entry,
  active,
  tablet,
  mobile,
  desktop,
  className,
  ...props
}) => {

  const { productLinks, otherLinks } = categorize(getField(entry, 'items', []));

  if (!productLinks.length && !otherLinks.length) return null;

  const hasProducts = !!productLinks.length;

  const headerTopic = getField(entry, 'headerTopic');

  return (
    <div className={cx(styles.drawerContent, className)} {...props}>
      {hasProducts && (
        <HeaderItem
          tabIndex={active ? undefined : -1}
          columnMode={desktop}
          className={cx(styles.col, styles.colSmall)}
          entry={headerTopic}
        />
      )}
      <ProductsGrid
        mobile={mobile}
        tablet={tablet}
        active={active}
        className={styles.col}
        entries={productLinks}
      />
      <MiscLinksGrid
        active={active}
        className={cx(styles.col, {
          [styles.colSmall]: hasProducts,
          [styles.colWithHighlight]: !hasProducts && headerTopic,
        })}
        entries={otherLinks}
        singleCol={hasProducts}
      />
      {!hasProducts && (
        <HeaderItem
          tabIndex={active ? undefined : -1}
          className={cx(styles.col, styles.colHighlight)}
          entry={headerTopic}
        />
      )}
    </div>
  );
};
